import { useCallback, useEffect, useRef } from 'react';

export function usePureCloudScript(isChatVisible, location = null) {
  const existingScript = document.querySelector(
    'script[src="https://apps.mypurecloud.com/genesys-bootstrap/genesys.min.js"]'
  );
  const originalDisplayStyles = useRef(new Map());

  const separateChatInstanceFlows = ['newonboarding', 'open-plan', 'add-a-beneficiary'];
  const isSeparateChatInstance = location && separateChatInstanceFlows.some(flow => location?.pathname?.includes(flow));
  const deploymentId = isSeparateChatInstance
    ? '233b283d-00ab-49d3-91b1-d901ab90ef83' // INFO: update deploymentId for onbarding, add-a-plan, add-a-bene as required
    : '7fa3ab77-d95b-4d6e-81e3-1fb1bd03275d';

  function findGenesysElements() {
    const elements = document.querySelectorAll(
      '[id^="genesys"], [class^="genesys"]'
    );
    return elements && elements.length > 0 ? elements : null;
  }

  function storeOriginalDisplayStyles(elements = findGenesysElements()) {
    if (!elements) return;
    elements.forEach((element) => {
      const displayStyle = getComputedStyle(element)?.display;
      if (!originalDisplayStyles.current?.has(element)) {
        originalDisplayStyles.current?.set(element, displayStyle);
      }
    });
  }

  function toggleChatIconVisibility(
    show = false,
    elements = findGenesysElements()
  ) {
    if (!elements) return;

    elements.forEach((element) => {
      const originalDisplayStyle =
        originalDisplayStyles.current?.get(element) || '';
      element.style.display = show ? originalDisplayStyle : 'none';
    });
  }

  const scriptLoadListener = useCallback(() => {
    storeOriginalDisplayStyles();
    toggleChatIconVisibility(isChatVisible);
  }, [isChatVisible, toggleChatIconVisibility]);

  const loadGenesysScript = useCallback(() => {
    window['_genesysJs'] = 'Genesys';
    window['Genesys'] =
      window['Genesys'] ||
      function () {
        (window['Genesys'].q = window['Genesys'].q || []).push(arguments);
      };
    window['Genesys'].t = 1 * new Date();
    window['Genesys'].c = {
      environment: 'prod',
      deploymentId: deploymentId,
      configuration: {
        messenger: {
          launcher: {
            enabled: true
          }
        }
      }
    };
    const script = document.createElement('script');
    script.async = true;
    script.src =
      'https://apps.mypurecloud.com/genesys-bootstrap/genesys.min.js';
    script.addEventListener('load', scriptLoadListener);
    document.head.appendChild(script);
    return () => script.removeEventListener('load', scriptLoadListener);
  }, [isChatVisible]);

  useEffect(() => {
    if (existingScript) {
      existingScript.addEventListener('load', scriptLoadListener);
      toggleChatIconVisibility(isChatVisible);
      return () =>
        existingScript.removeEventListener('load', scriptLoadListener);
    } else {
      loadGenesysScript();
      const observer = new MutationObserver(() => {
        storeOriginalDisplayStyles();
        toggleChatIconVisibility(isChatVisible);
      });
      observer.observe(document.body, { childList: true, subtree: true });
      return () => {
        observer.disconnect();
      };
    }
  }, [isChatVisible]);
}
